import { ACTOR_NAME_TO_KEY } from "@/lib/file-extraction/constants";
import type { RiskAnalysisSectorData } from "@/lib/file-extraction/types";
import type {
  BulkInsertRiskAnalysisItem,
  ImpactLevel,
  ProbabilityLevel,
  RiskMatrixData,
  SectorJustifications,
} from "@/types/risk-analysis";
import type { ModificationRow } from "@/utils/riskMatrixModifications";

export type MappedRiskAnalysisResult = {
  records: BulkInsertRiskAnalysisItem[];
  modificationsBySector: Record<string, ModificationRow[]>;
};

function normalizeImpact(raw: string): ImpactLevel {
  const lower = raw.trim().toLowerCase();
  if (lower.startsWith("sc")) return "Scăzut";
  if (lower.startsWith("rid") || lower.startsWith("ric")) return "Ridicat";
  return "Mediu";
}

function normalizeProbability(raw: string): ProbabilityLevel {
  const lower = raw.trim().toLowerCase();
  if (lower.startsWith("sc")) return "Scăzută";
  if (lower.startsWith("rid") || lower.startsWith("ric")) return "Ridicată";
  return "Medie";
}

function buildMatrix(sector: RiskAnalysisSectorData): RiskMatrixData {
  return {
    industry: sector.sectorId,
    entity_size_score: sector.dimensionScore,
    risk_assessment: sector.attackCategories.map((cat) => ({
      attack_category: cat.name,
      nature_of_attack: cat.attackNature,
      impact: normalizeImpact(cat.impact),
      threat_actors: Object.fromEntries(
        cat.actors.map((actor) => [
          ACTOR_NAME_TO_KEY[actor.name] ?? actor.name,
          {
            probability: normalizeProbability(actor.probability),
            score: actor.riskValue,
          },
        ])
      ),
    })),
  };
}

const HARDCODED_MODIFICATIONS: Record<string, ModificationRow[]> = {
  "101": [
    [
      "Foaia de lucru: 101_Energie / Celula: «D9». [I] a fost crescut.",
      "Da",
      "101ID9",
    ],
    [
      "Foaia de lucru: 101_Energie / Celula: «E10». [P] a fost crescut.",
      "Da",
      "101PE10",
    ],
    [
      "Foaia de lucru: 101_Energie / Celula: «K11». [P] a fost scăzut.",
      "Da",
      "101PK11",
    ],
  ],
  "201": [
    [
      "Foaia de lucru: 201_Servicii poștale / Celula: «D10». [I] a fost scăzut.",
      "Da",
      "201ID10",
    ],
    [
      "Foaia de lucru: 201_Servicii poștale / Celula: «G9». [P] a fost crescut.",
      "Da",
      "201PG9",
    ],
  ],
};

export function getHardcodedModifications(sectorId: string): ModificationRow[] {
  return HARDCODED_MODIFICATIONS[sectorId] ?? [];
}

export function buildJustificationsFromModifications(
  modifications: ModificationRow[]
): SectorJustifications {
  return Object.fromEntries(
    modifications.map((mod) => [mod[2], "da" as const])
  );
}

export function mapExtractedSectorsToBulkItems(
  sessionId: string,
  sectorResults: RiskAnalysisSectorData[]
): MappedRiskAnalysisResult {
  const records: BulkInsertRiskAnalysisItem[] = [];
  const modificationsBySector: Record<string, ModificationRow[]> = {};

  for (const sector of sectorResults) {
    const modifications = getHardcodedModifications(sector.sectorId);
    const justifications =
      modifications.length > 0
        ? buildJustificationsFromModifications(modifications)
        : {};

    records.push({
      session_id: sessionId,
      sector_id: sector.sectorId,
      score: sector.totalScore,
      level: sector.requirementCategory,
      matrix: buildMatrix(sector),
      justifications,
    });

    modificationsBySector[sector.sectorId] = modifications;
  }

  return { records, modificationsBySector };
}
